import React, { useContext, useState } from 'react';
import { useTranslations } from '@vp/om-translations';
import get from 'lodash/get';
import { Card, H2 } from '@vp/swan';
import { OrderSummary as OrderSummaryDisplay } from '@vp/component-library';
import { OrderDetailsContext } from '../../contexts/OrderDetailsContext';
import { useOrderSummaryItems } from '../../contexts/OrderDetailsSelectors';
import useSiteData from '../../../hooks/useSiteData';
import { paymentStates } from '../../../constants/paymentStates';
import { currencyLocaleFormatter } from '../../../util/currencyFormatter';
import { getTaxAwareOrderSummaryPricing } from '../../../util/taxDisplayPricingHelpers';
import { DiscountModal } from './DiscountModal';
import {
  getDiscountData,
  getFormattedDiscounts,
  getFormattedOrderPrice,
  getFormattedPriceSubtotal,
  getFormattedPriceSubtotalFromFee,
  getShippingLabel,
} from './orderSummaryHelper.ts';

/**
 * uses om-tax-display library to apply tax display rules (tax-inclusivity and display formatting).
 *
 * 1. Gets the product, and shipping pricing data from context
 * 2. Generates the tax aware product, shipping, and order totals from that pricing data
 * 3. Formats those totals for display
 * 4. Displays the totals
 */
export const OrderSummary = () => {
  const [orderSummaryMessages] = useTranslations(['orderSummaryMessages']);
  const {
    order,
    fractionDigits,
    itemIdToTaxInclusiveAndExclusivePricesMap,
    itemIdToTaxInclusiveAndExclusiveShippingPricesMap,
    currency,
    showShipmentDetails,
  } = useContext(OrderDetailsContext);
  const { itemSelectorCallback } = useOrderSummaryItems();
  const { locale } = useSiteData();
  const localeForCurrency = currencyLocaleFormatter(locale);

  const [itemDiscountModalOpen, setItemDiscountModalOpen] = useState(false);
  const [shippingDiscountModalOpen, setShippingDiscountModalOpen] =
    useState(false);

  const {
    shippingDiscounts,
    itemDiscounts,
    primaryItemDiscount,
    primaryShippingDiscount,
    showShippingDiscountModal,
    showItemDiscountModal,
  } = getDiscountData(order, locale);

  // determine order total field depending on paid vs. due, and gross order total price
  const payments = get(order, 'payments') || [];
  // TODO: move to orderSummaryHelper
  const orderHasBalanceDue =
    payments.filter(payment => payment.state === paymentStates.BALANCE_DUE)
      .length > 0;
  const orderTotalField = orderHasBalanceDue
    ? orderSummaryMessages.totalDueField
    : orderSummaryMessages.totalPaidField;

  // get pricing data (order total, shipping total, and product total) from an aggregate of lineItem taxAware prices
  const {
    taxAwareOrderTotalPrice,
    taxAwareProductTotalPrice,
    taxAwareShippingTotalPrice,
    shippingFees,
  } = getTaxAwareOrderSummaryPricing({
    itemIdToTaxInclusiveAndExclusivePricesMap,
    itemIdToTaxInclusiveAndExclusiveShippingPricesMap,
  });

  /* Format pricing objects and labels for orderTotal and product / shipping subtotals
  - labelFormat and priceFormat are TypographyDisplay object that drive styling of prices:
  - https://gitlab.com/vistaprint-org/order-management/libraries2/omt-component-library#taxawarepricerow-component */

  const subtotals = [];

  // if product price is present, include in the subtotals
  if (taxAwareProductTotalPrice) {
    // if discounts are present, include in subtotals
    const subtotalDiscountsOnClick = () => {
      setItemDiscountModalOpen(true);
    };
    const productSubtotalDiscounts = getFormattedDiscounts(
      taxAwareProductTotalPrice,
      orderSummaryMessages,
      subtotalDiscountsOnClick,
      primaryItemDiscount
    );

    // format "product price" subtotal for display using the component-library
    const formattedProductPrice = getFormattedPriceSubtotal(
      taxAwareProductTotalPrice,
      orderSummaryMessages.productTotalField,
      productSubtotalDiscounts
    );

    subtotals.push({
      ...formattedProductPrice,
      // product subtotal label and price should be bold
      labelFormat: { fontWeight: 'bold' },
      priceFormat: { fontWeight: 'bold' },
    });
  }

  if (showShipmentDetails && taxAwareShippingTotalPrice) {
    const shippingDiscountOnClick = () => {
      setShippingDiscountModalOpen(true);
    };
    const shippingSubtotalDiscounts = getFormattedDiscounts(
      taxAwareShippingTotalPrice,
      orderSummaryMessages,
      shippingDiscountOnClick,
      primaryShippingDiscount
    );

    // format "shipping price" subtotal for display using the component-library
    const formattedShippingPrice = getFormattedPriceSubtotal(
      taxAwareShippingTotalPrice,
      getShippingLabel(orderSummaryMessages, order, locale),
      shippingSubtotalDiscounts
    );

    subtotals.push(formattedShippingPrice);

    shippingFees.forEach(fee => {
      subtotals.push(getFormattedPriceSubtotalFromFee(fee));
    });
  }

  // format orderTotalPrice for display using the component-library
  const formattedOrderPrice = getFormattedOrderPrice(
    taxAwareOrderTotalPrice,
    orderTotalField
  );

  return (
    <Card bordered>
      {showItemDiscountModal ? (
        <DiscountModal
          open={itemDiscountModalOpen}
          setIsOpen={setItemDiscountModalOpen}
          discounts={{
            itemDiscounts,
            shippingDiscounts,
          }}
          selectedDiscountId={primaryItemDiscount.promotionId}
        />
      ) : null}
      {showShippingDiscountModal ? (
        <DiscountModal
          open={shippingDiscountModalOpen}
          setIsOpen={setShippingDiscountModalOpen}
          discounts={{
            itemDiscounts,
            shippingDiscounts,
          }}
          selectedDiscountId={primaryShippingDiscount.promotionId}
        />
      ) : null}
      <H2 mb={6} fontSkin="title-section" id="order-summary-section-heading">
        {orderSummaryMessages.titleMessage}
      </H2>
      <OrderSummaryDisplay
        fractionDigits={fractionDigits}
        order={formattedOrderPrice}
        locale={localeForCurrency}
        subtotals={subtotals}
        currencyCode={currency}
        itemSelector={itemSelectorCallback}
      />
    </Card>
  );
};
